/**
 * @module Accordion
 *
 * A vertically stacked set of interactive headings that reveal/hide associated content.
 * Built on Radix UI Accordion primitive with custom styling variants.
 *
 * @see {@link https://ui.shadcn.com/docs/components/accordion Shadcn Accordion}
 * @see {@link https://www.radix-ui.com/primitives/docs/components/accordion Radix Accordion}
 *
 * @example
 * // Basic single-select accordion
 * <Accordion type="single" collapsible>
 *   <AccordionItem value="item-1">
 *     <AccordionTrigger>Section 1</AccordionTrigger>
 *     <AccordionContent>Content for section 1</AccordionContent>
 *   </AccordionItem>
 * </Accordion>
 *
 * @example
 * // Multi-select accordion with HC variant (dark theme)
 * <Accordion type="multiple" variant="hc">
 *   <AccordionItem value="faq-1" variant="hc">
 *     <AccordionTrigger variant="hc">What is Uppbeat?</AccordionTrigger>
 *     <AccordionContent variant="hc">Royalty-free music platform</AccordionContent>
 *   </AccordionItem>
 * </Accordion>
 */
import { ChevronDown } from "lucide-react";
import { Accordion as AccordionPrimitive } from "radix-ui";
import type * as React from "react";
import { cn } from "../lib/utils";

/**
 * Visual style variants for the accordion.
 * - `default`: Light theme with subtle borders
 * - `hc`: High contrast dark theme variant
 */
type AccordionVariant = "default" | "hc";
type AccordionItemProps = React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item> & {
  variant?: AccordionVariant;
};
type AccordionProps = React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Root> & {
  variant?: AccordionVariant;
};

/**
 * Root container for accordion items. Controls single or multiple expansion.
 *
 * @param type - `"single"` allows one item open at a time, `"multiple"` allows many
 * @param collapsible - When `type="single"`, allows closing all items (default: false)
 * @param variant - Visual style: `"default"` or `"hc"` (high contrast)
 */
function Accordion({
  variant = "default",
  ...props
}: AccordionProps) {
  return <AccordionPrimitive.Root className={cn(variant === "hc" && "hc-accordion")} {...props} data-sentry-element="AccordionPrimitive.Root" data-sentry-component="Accordion" data-sentry-source-file="accordion.tsx" />;
}
Accordion.displayName = "Accordion";

/**
 * Wrapper for each accordion section. Must have a unique `value` prop.
 *
 * @param value - Unique identifier for this item (required)
 * @param variant - Visual style matching parent Accordion
 * @param disabled - Prevents interaction when true
 */
function AccordionItem({
  className,
  variant = "default",
  ...props
}: AccordionItemProps) {
  return <AccordionPrimitive.Item className={cn(variant === "default" ? "border-0 border-gray-150 border-b-1 border-solid bg-transparent transition-colors md:mb-1 dark:border-gray-800 [&:has(:focus-visible)]:ring-2 [&:has(:focus-visible)]:ring-[var(--focus-ring)] [&:has(:focus-visible)]:ring-offset-8 dark:[&:has(:focus-visible)]:ring-offset-gray-950" : "border-0 border-gray-800 border-b-1 border-solid bg-transparent transition-colors [&:has(:focus-visible)]:ring-2 [&:has(:focus-visible)]:ring-[var(--focus-ring)] [&:has(:focus-visible)]:ring-offset-8 [&:has(:focus-visible)]:ring-offset-gray-950 [&:has([data-state='open'])]:border-transparent", "last-of-type:border-b-0", className)} data-variant={variant} {...props} data-sentry-element="AccordionPrimitive.Item" data-sentry-component="AccordionItem" data-sentry-source-file="accordion.tsx" />;
}
AccordionItem.displayName = "AccordionItem";

/** Props for AccordionTrigger component */
interface AccordionTriggerProps extends React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger> {
  headerClassName?: string;
  triggerChildrenClassName?: string;
  variant?: AccordionVariant;
}

/**
 * Clickable heading that toggles the associated AccordionContent.
 * Includes an animated chevron indicator.
 *
 * @param variant - Visual style: `"default"` or `"hc"`
 * @param headerClassName - Additional classes for the header wrapper
 * @param triggerChildrenClassName - Additional classes for the inner content wrapper
 */
function AccordionTrigger({
  className,
  children,
  variant = "default",
  headerClassName,
  triggerChildrenClassName,
  ...props
}: AccordionTriggerProps) {
  return <AccordionPrimitive.Header className={cn("flex", headerClassName)} data-sentry-element="AccordionPrimitive.Header" data-sentry-component="AccordionTrigger" data-sentry-source-file="accordion.tsx">
      <AccordionPrimitive.Trigger className={cn(variant === "default" ? "group my-4 flex flex-1 cursor-pointer items-center justify-between text-left font-sans font-semibold text-base text-gray-950 transition-all md:my-3 2xl:my-2 2xl:text-lg dark:text-white [&>[data-icon='closed']]:data-[state=closed]:block [&>[data-icon='open']]:data-[state=open]:block [&>[data-icon]]:hidden" : "group my-3 flex flex-1 cursor-pointer items-center justify-between text-left font-sans font-semibold text-base text-white transition-all 2xl:text-lg [&>[data-icon='closed']]:data-[state=closed]:block [&>[data-icon='open']]:data-[state=open]:block [&>[data-icon]]:hidden", className)} data-variant={variant} style={{
      WebkitTapHighlightColor: "transparent"
    }} {...props} data-sentry-element="AccordionPrimitive.Trigger" data-sentry-source-file="accordion.tsx">
        <div className={cn("flex flex-1 items-center justify-between gap-1 rounded-xs py-2 font-semibold text-base transition-colors 2xl:text-lg", variant === "default" ? "md:px-2 md:hover:bg-gray-50 dark:md:hover:bg-gray-900" : "-my-1 px-2 hover:bg-gray-900", triggerChildrenClassName)}>
          {children}
          <ChevronDown className="w-5 transition-transform duration-150 group-data-[state=open]:rotate-180" data-sentry-element="ChevronDown" data-sentry-source-file="accordion.tsx" />
        </div>
      </AccordionPrimitive.Trigger>
    </AccordionPrimitive.Header>;
}
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;

/** Props for AccordionContent component */
interface AccordionContentProps extends React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content> {
  variant?: AccordionVariant;
}

/**
 * Collapsible content panel with smooth expand/collapse animation.
 * Automatically hidden when parent AccordionItem is closed.
 *
 * @param variant - Visual style: `"default"` or `"hc"`
 * @param forceMount - Keep content in DOM even when closed (useful for SEO/forms)
 */
function AccordionContent({
  className,
  children,
  variant = "default",
  ...props
}: AccordionContentProps) {
  return <AccordionPrimitive.Content className={cn(variant === "default" ? "overflow-hidden text-left font-sans text-gray-700 text-sm transition-all data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down md:px-2 2xl:text-base dark:text-gray-200" : "fl-px-6/12 overflow-hidden bg-gray-900 pt-6 text-left font-sans text-gray-400 text-sm transition-all data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down 2xl:text-base")} data-variant={variant} {...props} data-sentry-element="AccordionPrimitive.Content" data-sentry-component="AccordionContent" data-sentry-source-file="accordion.tsx">
      <div className={cn("pb-8", className)}>{children}</div>
    </AccordionPrimitive.Content>;
}
AccordionContent.displayName = AccordionPrimitive.Content.displayName;
export { Accordion, AccordionContent, type AccordionContentProps, AccordionItem, type AccordionItemProps, type AccordionProps, AccordionTrigger, type AccordionTriggerProps, type AccordionVariant };